/**
 * Attachments — choosing files to send, and listing files that were sent.
 *
 * The picker checks type, size and count against the server's own limits, so
 * a file that would be refused is refused here, before anything is uploaded.
 * The server checks again regardless, and also checks the contents.
 */

import { useQuery } from '@tanstack/react-query';
import { useId, useRef, useState } from 'react';

import { api, ApiError } from '../lib/api';

export interface AttachmentLimits {
  readonly maxFileBytes: number;
  readonly maxMessageBytes: number;
  readonly allowedExtensions: readonly string[];
  readonly maxFilesPerMessage: number;
}

export interface SentAttachment {
  readonly id: string;
  readonly originalFilename: string;
  readonly mimeType: string | null;
  readonly sizeBytes: number;
  readonly scanStatus: string;
}

/** Shown until the server's limits arrive; the server's are the ones that count. */
const FALLBACK_LIMITS: AttachmentLimits = {
  maxFileBytes: 25 * 1024 * 1024,
  maxMessageBytes: 100 * 1024 * 1024,
  allowedExtensions: ['pdf', 'docx', 'xlsx', 'pptx', 'jpg', 'jpeg', 'png', 'txt', 'csv', 'zip'],
  maxFilesPerMessage: 20,
};

export function useAttachmentLimits(): AttachmentLimits {
  const limits = useQuery({
    queryKey: ['attachment-limits'],
    queryFn: () => api.get<AttachmentLimits>('/api/v1/attachments/limits'),
    staleTime: 10 * 60_000,
  });
  return limits.data ?? FALLBACK_LIMITS;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
}

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot <= 0 ? '' : name.slice(dot + 1).toLowerCase();
}

function PaperclipIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="size-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M10.5 4.5 5.4 9.6a1.5 1.5 0 0 0 2.1 2.1l5.3-5.3a3 3 0 0 0-4.2-4.2L3.3 7.5a4.5 4.5 0 0 0 6.4 6.4l4-4" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Files chosen for a message that has not been sent yet.
 *
 * `files` is owned by the caller, which uploads them when the message is sent.
 */
export function FilePicker({
  files,
  onChange,
  disabled = false,
}: {
  files: readonly File[];
  onChange: (next: File[]) => void;
  disabled?: boolean;
}) {
  const limits = useAttachmentLimits();
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [problems, setProblems] = useState<string[]>([]);

  const total = files.reduce((sum, f) => sum + f.size, 0);
  const accepted = limits.allowedExtensions.map((e) => `.${e}`).join(',');

  function add(chosen: FileList | null) {
    if (chosen === null) return;
    const next = [...files];
    const refused: string[] = [];
    let bytes = total;

    for (const file of Array.from(chosen)) {
      const ext = extensionOf(file.name);
      if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
      if (!limits.allowedExtensions.includes(ext)) {
        refused.push(`${file.name}: ${ext === '' ? 'files without an extension' : `.${ext} files`} cannot be attached.`);
      } else if (file.size === 0) {
        refused.push(`${file.name} is empty.`);
      } else if (file.size > limits.maxFileBytes) {
        refused.push(`${file.name} is ${formatBytes(file.size)}; each file can be at most ${formatBytes(limits.maxFileBytes)}.`);
      } else if (next.length >= limits.maxFilesPerMessage) {
        refused.push(`${file.name}: a message can carry at most ${limits.maxFilesPerMessage} files.`);
      } else if (bytes + file.size > limits.maxMessageBytes) {
        refused.push(`${file.name}: the files on one message can total at most ${formatBytes(limits.maxMessageBytes)}.`);
      } else {
        next.push(file);
        bytes += file.size;
      }
    }

    setProblems(refused);
    onChange(next);
    // Lets the same file be chosen again after it is removed.
    if (input.current !== null) input.current.value = '';
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <label
          htmlFor={inputId}
          className={`interactive inline-flex items-center gap-1.5 border px-3 py-1.5 text-sm font-medium ${disabled ? 'cursor-not-allowed opacity-55' : 'cursor-pointer'}`}
          style={{
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-surface)',
            borderColor: 'var(--color-rule-strong)',
            color: 'var(--color-ink)',
          }}
        >
          <PaperclipIcon />
          Attach files
        </label>
        <input
          ref={input}
          id={inputId}
          type="file"
          multiple
          accept={accepted}
          disabled={disabled}
          onChange={(e) => add(e.target.files)}
          className="sr-only"
        />
        <span className="text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
          {limits.allowedExtensions.map((e) => e.toUpperCase()).join(', ')} · up to {formatBytes(limits.maxFileBytes)} each
        </span>
      </div>

      {files.length > 0 && (
        <ul className="mt-2 divide-y border" style={{ borderColor: 'var(--color-rule)', borderRadius: 'var(--radius-sm)' }}>
          {files.map((file) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm"
              style={{ borderColor: 'var(--color-rule)' }}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <PaperclipIcon />
                <span className="truncate">{file.name}</span>
                <span className="tabular shrink-0 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
                  {formatBytes(file.size)}
                </span>
              </span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(files.filter((f) => f !== file))}
                className="interactive shrink-0 px-1.5 text-xs font-medium disabled:opacity-55"
                style={{ color: 'var(--color-danger)' }}
                aria-label={`Remove ${file.name}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {problems.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 text-xs" style={{ color: 'var(--color-danger)' }} role="alert">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Files sent with a message, each downloadable by anyone who can see the message. */
export function AttachmentList({
  attachments,
  onError,
}: {
  attachments: readonly SentAttachment[];
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  if (attachments.length === 0) return null;

  async function download(file: SentAttachment) {
    setBusy(file.id);
    try {
      await api.download(`/api/v1/attachments/${file.id}/download`, file.originalFilename);
    } catch (caught) {
      onError(
        caught instanceof ApiError
          ? (caught.problem?.detail ?? `Could not download ${file.originalFilename}.`)
          : 'Could not reach the server.',
      );
    } finally {
      setBusy(null);
    }
  }

  return (
    <ul className="mt-2 flex flex-wrap gap-2" aria-label="Attached files">
      {attachments.map((file) => (
        <li key={file.id}>
          <button
            type="button"
            onClick={() => void download(file)}
            disabled={busy === file.id}
            className="interactive inline-flex max-w-full items-center gap-1.5 border px-2.5 py-1 text-sm disabled:opacity-55"
            style={{
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface)',
              borderColor: 'var(--color-rule-strong)',
              color: 'var(--color-accent)',
            }}
            title={`Download ${file.originalFilename}`}
          >
            <PaperclipIcon />
            <span className="truncate">{file.originalFilename}</span>
            <span className="tabular shrink-0 text-xs" style={{ color: 'var(--color-ink-subtle)' }}>
              {busy === file.id ? 'Downloading…' : formatBytes(file.sizeBytes)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}
